/**
 * GameSession owns everything about "the currently running game" that isn't
 * pure simulation: the sim state itself, the local command queue, the
 * network connection (multiplayer), fog-of-war visibility, toasts, and the
 * ephemeral UI state (camera/selection/placement/drag-box) that the renderer
 * and input controller read and mutate directly.
 *
 * Solo mode drives the sim with a local 8Hz timer. Multiplayer mode applies
 * ticks exactly as broadcast by the room's Durable Object, in order.
 */

import {
  BUILDINGS,
  UNITS,
  applyTick,
  createGame,
  entityCenter,
  getEntity,
  TICKS_PER_SECOND,
} from '../sim';
import type {
  BotDifficulty,
  BuildingType,
  Command,
  CopilotMode,
  EntityId,
  FactionId,
  GameConfig,
  GameEvent,
  GameState,
  IssuedCommand,
  LobbySlot,
  PlayerIdx,
  ServerMessage,
  Vec2,
} from '../sim';
import { ACHIEVEMENTS } from '../shared/achievements';
import * as Audio from './audio';
import { viewportTiles, type Camera } from './camera';
import { NetClient } from './net';

export type SessionMode = 'solo' | 'multiplayer';
export type ToastKind = 'info' | 'warn' | 'danger';
/** Cyber-offense abilities the player can arm from an Adversarial Lab. */
export type CyberAbility = 'promptInjection' | 'ddos' | 'dataPoison';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  expiresAt: number;
  /** how many consecutive identical events have been coalesced into this toast */
  count: number;
  /** anomaly toasts (mirroring the always-visible Terminate banner) are never
   *  coalesced or evicted by the visible-toast cap, and always render on top */
  anomaly?: boolean;
  /** AI Copilot advisory messages: distinctive styling, longer TTL, and — like
   *  anomaly toasts — never coalesced with anything else or evicted by the cap. */
  copilot?: boolean;
}

export interface LobbyState {
  slots: LobbySlot[];
  you: number;
}

export interface DragBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface PlacementState {
  building: BuildingType;
  engineerId: EntityId;
  cursor: Vec2;
  /** touch flow: first tap positions the ghost ("armed"), second tap on the
   * same tile confirms — desktop mouse still places on first click */
  armed?: boolean;
}

export class GameSession {
  state: GameState | null = null;
  mode: SessionMode = 'solo';
  myPlayer: PlayerIdx = 0;

  // UI-only ephemeral state, read/written by input.ts and renderer.ts
  camera: Camera = { x: 0, y: 0, zoom: 1 };
  selection: EntityId[] = [];
  placement: PlacementState | null = null;
  dragBox: DragBox | null = null;
  /** Armed cyber attack awaiting a target click on an enemy building. Set from
   *  the Adversarial Lab panel; consumed/cleared by the next map click. */
  cyberTarget: CyberAbility | null = null;
  /** A combat order armed from the selection panel, awaiting target click(s):
   *  'guard' needs one click on a friendly unit/building; 'patrol' needs two
   *  map-point clicks (the first is stored in `a`). Applied to the current
   *  selection. Cleared/consumed by input.ts. */
  pendingOrder: { kind: 'guard' } | { kind: 'patrol'; a: Vec2 | null } | null = null;

  /** Set by queueCommand whenever a 'build' command matching the currently
   *  active placement lands (manual tap-to-place or Quick Build). The hud's
   *  sticky-placement handling (Shift held / "repeat" toggle) reads this
   *  right after placement mode exits to decide what to re-arm — see
   *  Hud.maybeRearmPlacement. Cleared every time placement mode ends,
   *  sticky or not, so a later plain cancel never resurrects stale data. */
  lastManualBuild: { building: BuildingType; engineerId: EntityId } | null = null;

  // Fog of war (recomputed once per sim-second, not per frame)
  visible: Uint8Array = new Uint8Array(0);
  explored: Uint8Array = new Uint8Array(0);

  toasts: Toast[] = [];
  activeAnomalies: Set<EntityId> = new Set();

  /** Achievements unlocked by THIS match, as relayed by the room after the
   * match report (ranked accounts only). Rendered on the game-over panel. */
  newAchievements: string[] = [];

  // Multiplayer
  lobby: LobbyState | null = null;
  lastError: string | null = null;
  /** Room code this session connected to (multiplayer only); used to build
   *  the AI Copilot MCP endpoint URL. */
  roomCode: string | null = null;
  /** This seat's own AI Copilot bearer token, delivered only to this socket
   *  on the 'lobby' and 'start' server messages. Never sent for other seats. */
  copilotToken: string | null = null;

  onStart: (() => void) | null = null;
  onLobbyUpdate: (() => void) | null = null;

  private net: NetClient | null = null;
  private tickTimer: number | null = null;
  private pendingCommands: Command[] = [];
  private serverTickQueue: IssuedCommand[][] = [];
  private toastSeq = 0;

  // Reconnection (multiplayer): join parameters are kept so an unexpected
  // socket close can transparently re-join; the server reattaches us to our
  // seat and resyncs (see room.ts handleRejoin).
  private joinName = '';
  private joinFaction: FactionId = 'hyperscale';
  private joinAccount: { secret: string } | undefined;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private destroyed = false;
  /** True once this session has entered the game screen ('start' or a
   * fresh-page 'resync'); a mid-game resync must NOT re-fire onStart —
   * main.ts's enterGame is not idempotent. */
  private gameStarted = false;

  private static readonly MAX_RECONNECT_ATTEMPTS = 8;
  private static readonly RECONNECT_DELAY_MS = 1500;

  // ---------------------------------------------------------------------
  // Solo
  // ---------------------------------------------------------------------

  startSolo(config: GameConfig, myIdx: PlayerIdx): void {
    this.mode = 'solo';
    this.myPlayer = myIdx;
    this.state = createGame(config);
    this.initVisibility();
    this.recomputeVisibility();
    this.centerCameraOnHq();
    this.tickTimer = window.setInterval(() => this.soloTick(), 1000 / TICKS_PER_SECOND);
  }

  private soloTick(): void {
    if (!this.state || this.state.winner !== null) return;
    const cmds: IssuedCommand[] = this.pendingCommands.map((cmd) => ({ player: this.myPlayer, cmd }));
    this.pendingCommands = [];
    applyTick(this.state, cmds);
    this.handlePostTick();
  }

  // ---------------------------------------------------------------------
  // Multiplayer
  // ---------------------------------------------------------------------

  connectMultiplayer(
    roomCode: string,
    name: string,
    faction: FactionId,
    account?: { secret: string },
  ): void {
    this.mode = 'multiplayer';
    this.roomCode = roomCode;
    this.joinName = name;
    this.joinFaction = faction;
    this.joinAccount = account;
    this.openSocket();
  }

  private openSocket(): void {
    const net = new NetClient(this.roomCode!);
    this.net = net;
    net.onOpen = () => {
      this.reconnectAttempts = 0;
      net.send({ type: 'join', name: this.joinName, faction: this.joinFaction, account: this.joinAccount });
    };
    net.onMessage = (msg) => this.handleServerMessage(msg);
    net.onClose = (ev) => this.handleSocketClose(ev);
    net.onError = () => {
      // A browser WebSocket 'error' is always followed by 'close';
      // reconnection is handled there.
    };
  }

  private handleSocketClose(ev: CloseEvent): void {
    if (this.destroyed || this.mode !== 'multiplayer') return;
    // 4xxx are the server's deliberate application-level rejections (room
    // full, game in progress with no seat for us) — retrying won't help.
    const rejected = ev.code >= 4000 && ev.code < 5000;
    const gameOver = this.state !== null && this.state.winner !== null;
    if (rejected || gameOver || this.reconnectAttempts >= GameSession.MAX_RECONNECT_ATTEMPTS) {
      this.lastError = 'Disconnected from server.';
      this.pushToast('danger', 'Disconnected from server.');
      this.onLobbyUpdate?.();
      return;
    }
    this.reconnectAttempts += 1;
    this.pushToast(
      'warn',
      `Connection lost — reconnecting (${this.reconnectAttempts}/${GameSession.MAX_RECONNECT_ATTEMPTS})…`,
    );
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, GameSession.RECONNECT_DELAY_MS);
    this.onLobbyUpdate?.();
  }

  setFaction(faction: FactionId): void {
    this.net?.send({ type: 'setFaction', faction });
  }

  setReady(ready: boolean): void {
    this.net?.send({ type: 'ready', ready });
  }

  hostStart(): void {
    this.net?.send({ type: 'start' });
  }

  /** Host only. Fills the next open lobby slot with a bot. */
  addBot(faction?: FactionId, difficulty?: BotDifficulty): void {
    this.net?.send({ type: 'addBot', faction, difficulty });
  }

  /** Host only. Pops the most recently added bot slot, if any. */
  removeBot(): void {
    this.net?.send({ type: 'removeBot' });
  }

  /** Sets this seat's own AI Copilot permission clearance. */
  setCopilotMode(mode: CopilotMode): void {
    this.net?.send({ type: 'copilotMode', mode });
  }

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'lobby':
        this.lobby = { slots: msg.slots, you: msg.you };
        if (msg.copilotToken) this.copilotToken = msg.copilotToken;
        this.onLobbyUpdate?.();
        break;
      case 'start':
        this.myPlayer = msg.you;
        if (msg.copilotToken) this.copilotToken = msg.copilotToken;
        this.newAchievements = [];
        this.state = createGame(msg.config);
        this.initVisibility();
        this.recomputeVisibility();
        this.centerCameraOnHq();
        this.gameStarted = true;
        this.onStart?.();
        break;
      case 'resync': {
        this.myPlayer = msg.you;
        if (msg.copilotToken) this.copilotToken = msg.copilotToken;
        // Anything still queued predates the resync and is contained in the
        // replayed history — applying it twice would break lockstep.
        this.serverTickQueue = [];
        // Fast-forward silently: no per-tick toasts/audio, just state.
        const state = createGame(msg.config);
        for (const cmds of msg.history) applyTick(state, cmds);
        this.state = state;
        this.initVisibility();
        this.recomputeVisibility();
        this.centerCameraOnHq();
        this.activeAnomalies.clear();
        for (const e of state.entities) {
          if (e.kind === 'unit' && e.owner === this.myPlayer && e.anomalyTicks > 0) {
            this.activeAnomalies.add(e.id);
          }
        }
        this.pushToast('info', 'Reconnected — resynced to the live game.');
        if (!this.gameStarted) {
          this.gameStarted = true;
          this.onStart?.();
        }
        break;
      }
      case 'tick':
        this.serverTickQueue.push(msg.commands);
        break;
      case 'playerLeft':
        this.pushToast('warn', `Player ${msg.player + 1} disconnected.`);
        this.onLobbyUpdate?.();
        break;
      case 'playerRejoined':
        if (msg.player !== this.myPlayer) {
          this.pushToast('info', `Player ${msg.player + 1} reconnected.`);
        }
        break;
      case 'ping':
        this.net?.send({ type: 'pong', tick: this.state?.tick ?? msg.tick });
        break;
      case 'error':
        this.lastError = msg.message;
        this.pushToast('danger', msg.message);
        this.onLobbyUpdate?.();
        break;
      case 'copilot':
        // A distinctive, longer-lived toast for the AI Copilot's own advice
        // — never coalesced with ordinary game toasts (see pushToast/capToasts).
        this.pushToast('info', msg.message, { copilot: true });
        break;
      case 'achievements':
        // Freshly unlocked ranked achievements, relayed right after the
        // match report lands. Toasts plug into the normal alert pipeline;
        // the game-over panel (hud.ts) shows the same list persistently.
        this.newAchievements = [...this.newAchievements, ...msg.ids];
        for (const id of msg.ids) {
          const def = ACHIEVEMENTS[id];
          if (def) {
            this.pushToast('info', `🏆 Achievement unlocked: ${def.emoji} ${def.name}`, { ttlMs: 12_000 });
          }
        }
        break;
    }
  }

  /** Apply every buffered server tick, in order. Call once per animation frame. */
  drainNetworkTicks(): void {
    if (this.serverTickQueue.length === 0) return;
    const batch = this.serverTickQueue;
    this.serverTickQueue = [];
    for (const cmds of batch) {
      if (!this.state) break;
      applyTick(this.state, cmds);
      this.handlePostTick();
    }
  }

  // ---------------------------------------------------------------------
  // Shared
  // ---------------------------------------------------------------------

  /** Queue a command issued by the local player. */
  queueCommand(cmd: Command): void {
    if (
      cmd.type === 'build' &&
      this.placement &&
      this.placement.building === cmd.building &&
      this.placement.engineerId === cmd.unitId
    ) {
      this.lastManualBuild = { building: cmd.building, engineerId: cmd.unitId };
    }
    if (this.mode === 'solo') {
      this.pendingCommands.push(cmd);
    } else if (this.net && this.state) {
      this.net.send({ type: 'commands', tick: this.state.tick, commands: [cmd] });
    }
  }

  private handlePostTick(): void {
    if (!this.state) return;
    for (const ev of this.state.events) this.handleEvent(ev);
    Audio.onTick(this.state, this.myPlayer);
    if (this.state.tick % TICKS_PER_SECOND === 0) this.recomputeVisibility();
    // drop anomaly alerts for units that no longer exist
    for (const id of this.activeAnomalies) {
      if (!getEntity(this.state, id)) this.activeAnomalies.delete(id);
    }
    this.pruneToasts();
  }

  private handleEvent(ev: GameEvent): void {
    const mine = ev.player === this.myPlayer;
    const global = ev.player === -1; // world events (market migration, data respawn) — everyone's toasts
    switch (ev.type) {
      case 'anomalyStarted':
        if (mine && ev.entityId !== undefined) this.activeAnomalies.add(ev.entityId);
        // Marked 'anomaly' so it always renders above other toasts and is
        // never coalesced away — the persistent Terminate banner is driven
        // separately by activeAnomalies, but this notice should still stand
        // out as it lands.
        if (mine) this.pushToast('danger', ev.message, { anomaly: true });
        break;
      case 'anomalyCleared':
        if (ev.entityId !== undefined) this.activeAnomalies.delete(ev.entityId);
        if (mine) this.pushToast('info', ev.message);
        break;
      case 'insolvencyWarning':
        if (mine) this.pushToast('danger', ev.message);
        break;
      case 'underAttack':
        // Attacks fire an event repeatedly while ongoing; coalescing (below)
        // turns repeats into a single "xN" toast instead of a stack, and a
        // short TTL keeps it from lingering after the raid ends.
        if (mine) this.pushToast('danger', ev.message, { ttlMs: 4000 });
        break;
      case 'phaseAdvanced':
      case 'techResearched':
      case 'doctrineChosen':
      case 'fineTuneComplete':
      case 'modelShipped':
        if (mine) this.pushToast('info', ev.message);
        break;
      case 'frontierAdvanced':
        // The frontier moving reprices EVERY player's fleet — this toast is
        // for the whole lobby, not just the shipper.
        this.pushToast(mine ? 'info' : 'warn', `🚀 ${ev.message}`, { ttlMs: 8000 });
        break;
      case 'brownout':
        // The persistent HUD banner (driven straight off powerDraw > powerCap
        // in hud.ts) already communicates the ongoing state; this toast is
        // just the "it just started" ping, same tier as underAttack.
        if (mine) this.pushToast('warn', ev.message);
        break;
      case 'brownoutEnded':
        if (mine) this.pushToast('info', ev.message);
        break;
      case 'playerEliminated':
        this.pushToast(mine ? 'danger' : 'warn', ev.message);
        break;
      case 'gameOver':
        this.pushToast('info', ev.message);
        break;
      case 'cyberAttack':
        // Victim-facing: someone hit us with a cyber op. Treat like an anomaly
        // alert — stands out, never coalesced.
        if (mine) this.pushToast('danger', `🛡 ${ev.message}`, { anomaly: true });
        break;
      case 'cyberDefended':
        if (mine) this.pushToast('info', `🛡 ${ev.message}`);
        break;
      case 'marketMoved':
        // Global (player: -1) event: a market segment relocated. Reaches
        // every player's toasts, not just whoever's map happened to touch it.
        if (global) this.pushToast('info', '📈 Market demand shifted!', { ttlMs: 4000 });
        break;
      case 'dataDiscovered':
        if (global) this.pushToast('info', '🗃 Fresh dataset dropped — new data node!', { ttlMs: 6000 });
        break;
    }
  }

  /** Max number of non-anomaly toasts visible at once; oldest is evicted first. */
  private static readonly MAX_VISIBLE_TOASTS = 3;

  pushToast(kind: ToastKind, message: string, opts?: { ttlMs?: number; anomaly?: boolean; copilot?: boolean }): void {
    const anomaly = opts?.anomaly ?? false;
    const copilot = opts?.copilot ?? false;
    const ttlMs = opts?.ttlMs ?? (copilot ? 10000 : kind === 'danger' ? 9000 : 5000);
    const expiresAt = Date.now() + ttlMs;

    // Coalesce with the most recently added toast if it's an identical,
    // still-live, non-anomaly, non-copilot message: bump its counter and
    // refresh its timer instead of stacking a duplicate. Copilot advice is
    // never coalesced, even with another copilot message, so each piece of
    // advice gets its own visible card.
    const last = this.toasts[this.toasts.length - 1];
    if (
      !anomaly &&
      !copilot &&
      last &&
      !last.anomaly &&
      !last.copilot &&
      last.kind === kind &&
      last.message === message
    ) {
      last.count += 1;
      last.expiresAt = expiresAt;
      return;
    }

    this.toasts.push({ id: this.toastSeq++, kind, message, expiresAt, count: 1, anomaly, copilot });
    this.capToasts();
  }

  /** Keep at most MAX_VISIBLE_TOASTS ordinary toasts, evicting the oldest
   *  first. Anomaly and copilot toasts are exempt — they're rare, important,
   *  and self-clear on their own (longer) TTL. */
  private capToasts(): void {
    const capped = this.toasts.filter((t) => !t.anomaly && !t.copilot);
    const excess = capped.length - GameSession.MAX_VISIBLE_TOASTS;
    if (excess <= 0) return;
    const evictIds = new Set(capped.slice(0, excess).map((t) => t.id));
    this.toasts = this.toasts.filter((t) => !evictIds.has(t.id));
  }

  private pruneToasts(): void {
    const now = Date.now();
    this.toasts = this.toasts.filter((t) => t.expiresAt > now);
  }

  hasFocusAdoption(): boolean {
    return !!this.state?.players[this.myPlayer]?.techs.includes('focusAdoption');
  }

  /** Center the camera on the local player's own HQ (Cloud Console) so every
   * player's opening view is their own base, not the map's top-left corner
   * (which is player 0's spawn). The game canvas is fullscreen, so window
   * dimensions approximate the viewport; the renderer's per-frame
   * clampCamera trues the result up against the real canvas size. */
  private centerCameraOnHq(): void {
    if (!this.state) return;
    const hq = this.state.entities.find(
      (e) => e.kind === 'building' && e.type === 'hq' && e.owner === this.myPlayer,
    );
    if (!hq) return;
    const center = entityCenter(hq);
    const { vw, vh } = viewportTiles(window.innerWidth, window.innerHeight, this.camera.zoom);
    this.camera.x = center.x - vw / 2;
    this.camera.y = center.y - vh / 2;
  }

  private initVisibility(): void {
    if (!this.state) return;
    const { width, height } = this.state.map;
    this.visible = new Uint8Array(width * height);
    this.explored = new Uint8Array(width * height);
  }

  recomputeVisibility(): void {
    if (!this.state) return;
    const { width, height } = this.state.map;
    const vis = new Uint8Array(width * height);
    for (const e of this.state.entities) {
      if (e.owner !== this.myPlayer) continue;
      let sight = 0;
      let center: Vec2;
      if (e.kind === 'unit') {
        sight = UNITS[e.type].sight;
        center = e.pos;
      } else if (e.kind === 'building') {
        sight = e.buildTicks === 0 ? BUILDINGS[e.type].sight : 4;
        center = entityCenter(e);
      } else {
        continue;
      }
      const r = Math.ceil(sight);
      const cx = Math.floor(center.x);
      const cy = Math.floor(center.y);
      const r2 = sight * sight;
      for (let dy = -r; dy <= r; dy++) {
        const y = cy + dy;
        if (y < 0 || y >= height) continue;
        for (let dx = -r; dx <= r; dx++) {
          const x = cx + dx;
          if (x < 0 || x >= width) continue;
          if (dx * dx + dy * dy > r2) continue;
          const i = y * width + x;
          vis[i] = 1;
          this.explored[i] = 1;
        }
      }
    }
    this.visible = vis;
  }

  fogAt(tx: number, ty: number): 0 | 1 | 2 {
    if (!this.state) return 0;
    const { width, height } = this.state.map;
    if (tx < 0 || ty < 0 || tx >= width || ty >= height) return 0;
    const i = ty * width + tx;
    if (this.visible[i]) return 2;
    if (this.explored[i]) return 1;
    return 0;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.tickTimer !== null) {
      window.clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.net?.close();
    this.net = null;
  }
}
